import { create } from 'zustand'
import { LocalStorageKey } from '@/constants/localstorage'
import { roundFiatBet, formatFiatDisplay } from '@/helpers/fiatUtils'
import posthog from 'posthog-js'

export type FiatRatesData = {
  rates: Record<string, number>
  symbols: Record<string, string>
  names: Record<string, string>
}

interface FiatCurrencyState extends FiatRatesData {
  selectedCurrency: string
  isLoaded: boolean

  setCurrency: (code: string) => void
  loadRates: (data: FiatRatesData) => void

  usdcToFiat: (usdc: number) => number
  fiatToUsdc: (fiat: number) => number

  formatBalance: (usdc: number) => string
  formatBetMin: (usdcMin: number) => string
  formatBetMax: (usdcMax: number) => string

  isUsd: () => boolean
  getSymbol: () => string
  getCode: () => string
}

export const useFiatCurrencyStore = create<FiatCurrencyState>((set, get) => ({
  selectedCurrency: 'USD',
  isLoaded: false,
  rates: { USD: 1 },
  symbols: { USD: '$' },
  names: { USD: 'US Dollar' },

  setCurrency: (code) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LocalStorageKey.FiatCurrency, code)
      posthog.capture('fiat_currency_changed', { currency: code, type: 'fiat' })
    }
    set({ selectedCurrency: code })
  },

  loadRates: (data) => {
    const saved =
      typeof window !== 'undefined' ? localStorage.getItem(LocalStorageKey.FiatCurrency) : null
    set({
      ...data,
      isLoaded: true,
      selectedCurrency: saved && data.rates[saved] ? saved : 'USD',
    })
  },

  usdcToFiat: (usdc) => {
    const { rates, selectedCurrency } = get()
    return usdc * (rates[selectedCurrency] ?? 1)
  },

  fiatToUsdc: (fiat) => {
    const { rates, selectedCurrency } = get()
    const rate = rates[selectedCurrency] ?? 1
    return fiat / rate
  },

  formatBalance: (usdc) => {
    const { selectedCurrency, symbols } = get()
    const fiat = get().usdcToFiat(usdc)
    return formatFiatDisplay(fiat, symbols[selectedCurrency] ?? selectedCurrency)
  },

  formatBetMin: (usdcMin) => {
    const { selectedCurrency, symbols } = get()
    const fiat = get().usdcToFiat(usdcMin)
    const rounded = roundFiatBet(fiat, 'up')
    return formatFiatDisplay(rounded, symbols[selectedCurrency] ?? selectedCurrency)
  },

  formatBetMax: (usdcMax) => {
    const { selectedCurrency, symbols } = get()
    const fiat = get().usdcToFiat(usdcMax)
    const rounded = roundFiatBet(fiat, 'down')
    return formatFiatDisplay(rounded, symbols[selectedCurrency] ?? selectedCurrency)
  },

  isUsd: () => get().selectedCurrency === 'USD',
  getSymbol: () => get().symbols[get().selectedCurrency] ?? get().selectedCurrency,
  getCode: () => get().selectedCurrency,
}))
